import React, { Component } from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import find from 'lodash/find';
import memoize from 'fast-memoize';
import orderBy from 'lodash/orderBy';
import moment from 'moment';
import { prop, ifProp } from 'styled-tools';
import WindcreekTabs from 'components/WindcreekTabs';
import WindcreekTab from 'components/WindcreekTab';
import WaitlistFormContainer from '../containers/WaitlistFormContainer';
import PromotionBanner from './PromotionBanner';
import CurrentOffers from './CurrentOffers';
import PastOffers from './PastOffers';
import { PROPERTY_BETHLEHEM } from 'utils/constants';
import { WCH_HOTEL } from 'utils/rewards';
import {
  hotelOfferShape,
  errorShape,
  submittedAccountShape
} from 'utils/shapes';
import { isHostBookingSource } from 'utils/routeUtils';
import { isMobileUserAgent } from 'utils/mobile';

const StyledOffers = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  margin-bottom: 1em;
  min-height: 7em;

  .offers-header {
    margin: 0;

    .your-offers {
      padding: 0;
      ${ifProp(
        'isHostBooking',
        css`
          width: 100%;
        `
      )}

      h5 {
        background-color: ${props =>
          props.theme.buttons.primary.backgroundColor};
        color: ${props => props.theme.colors.white};
        padding: 0.5em;
        display: inline-block;
        border-radius: 0.25em 0.25em 0 0;
        height: 100%;
        margin: 0;
      }
    }
    .promotion-container {
      height: 100%;
      padding: 0 0 0.3em 0.3em;
    }
  }

  div.offers {
    background-color: ${props => props.theme.buttons.primary.backgroundColor};
    color: ${props => props.theme.colors.white};
    padding: 0.5em;
    flex: 1 0 auto;
    display: flex;
    flex-direction: column;

    .offers-list {
      flex: 1 0 auto;
    }
  }

  @media screen and (min-width: ${props => props.theme.breakpoints.medium}) {
    margin-bottom: 0;
  }

  @media screen and (min-width: ${props => props.theme.breakpoints.large}) {
    min-height: 10.5em;
  }

  table {
    margin: 0;
    font-size: 0.9em;
    color: ${props => props.theme.colors.white};

    th,
    td {
      padding: 0.05rem 0.5rem;
      color: ${props => props.theme.colors.white};
      font-size: 1em;
      min-width: 8rem;

      ${ifProp(
        'isHostBooking',
        css`
          @media screen and (max-width: ${prop(
              'theme.breakpoints.extraLarge'
            )}) {
            font-size: 0.75em;
          }
        `
      )}
    }

    th {
      vertical-align: top;
      font-size: 1.1em;

      ${ifProp(
        'isHostBooking',
        css`
          @media screen and (max-width: ${prop(
              'theme.breakpoints.extraLarge'
            )}) {
            font-size: 0.85em;
          }
        `
      )}
    }

    tr {
      &.selected {
        background: #fff;
        td {
          color: ${props => props.theme.buttons.primary.backgroundColor};
        }
      }
      &.rowOffer:hover {
        cursor: pointer;
        background-color: ${props => props.theme.table.hoverRowBackgroundColor};
      }
      &.inactive {
        pointer-events: none;

        td {
          opacity: 0.7;
        }
      }
    }
  }

  .scrollable {
    &.extraHeight {
      tbody {
        max-height: 14rem;
      }
    }
    thead {
      display: block;
    }
    tbody {
      max-height: 5rem;
      display: block;
      overflow-y: scroll;

      ${ifProp(
        'isHostBooking',
        css`
          max-height: 18rem;
          overflow: auto;
        `
      )}
    }

    thead,
    tbody {
      tr {
        display: table;
        width: 100%;
        table-layout: fixed;

        th,
        td {
          width: 60%;

          &:first-child {
            width: 25%;
          }

          &:last-child {
            width: 30%;
          }
        }
      }
    }
  }
`;

const StyledWindcreekTabs = styled(WindcreekTabs)`
  background-color: ${props => props.theme.buttons.primary.backgroundColor};
`;

const StyledWindcreekTab = styled(WindcreekTab)`
  /* Required to override WindcreekTab styling */
  &&& {
    margin: 0.5em;

    &:not(.property-item) {
      margin-top: 0.5em;
    }
  }

  @media screen and (max-width: ${props =>
      props.theme.breakpoints.extraSmall}) {
    font-size: 0.75rem;
  }
`;

// -----------------------------------------------------
// Consts and helper functions
// -----------------------------------------------------
const CURRENT_OFFERS_TAB = 0;
const PAST_USED_OFFERS_TAB = 1;
const REQUEST_WAIT_LIST_TAB = 2;

function isOfferSelected(offer, selectedOffers) {
  return !!find(
    Object.values(selectedOffers),
    selectedOffer =>
      selectedOffer &&
      selectedOffer.patronReward.prizeCode === offer.patronReward.prizeCode
  );
}

const combineOffersWithSelectedMeta = memoize((offers, selectedOffers) => {
  if (!offers || offers.length === 0) {
    return offers;
  }

  return offers.map(offer =>
    Object.assign({}, offer, {
      isSelected: isOfferSelected(offer, selectedOffers)
    })
  );
});

class Offers extends Component {
  constructor(props) {
    super(props);

    this.state = {
      activeTab: CURRENT_OFFERS_TAB
    };
  }

  componentDidMount() {
    if (this.props.isWaitListBooking) {
      this.setActiveTab(REQUEST_WAIT_LIST_TAB);
    }
  }

  setActiveTab = tab => {
    this.setState({
      activeTab: tab
    });
  };

  handleSelectedOffer = (offer, autoSelectedOffers) => {
    const { setAdminSelectedOffers, adminSelectedOffers } = this.props;

    // If adminSelectedOffers === null, that means the host hasn't selected one yet.
    // In that case, we use the autoSelectedOffers as the initial state.
    const selectedOffers =
      adminSelectedOffers === null
        ? autoSelectedOffers.filter(offer => offer.isSelected)
        : adminSelectedOffers;

    const isSelected = !!find(
      selectedOffers,
      selectedOffer =>
        selectedOffer.patronReward.prizeCode === offer.patronReward.prizeCode
    );

    if (!isSelected) {
      setAdminSelectedOffers([...selectedOffers, offer]);
    } else {
      setAdminSelectedOffers(
        selectedOffers.filter(
          selectedOffer =>
            selectedOffer.patronReward.prizeCode !==
            offer.patronReward.prizeCode
        )
      );
    }
  };

  sortOffers = offers => {
    const { startDate, endDate, bookingSource, isAdmin } = this.props;

    const sortFields = isAdmin
      ? ['isSelectable', 'bookingStartDate', 'bookingEndDate']
      : ['isSelected', 'bookingStartDate', 'bookingEndDate'];
    const sortDirects = isAdmin
      ? ['desc', 'asc', 'asc']
      : ['desc', 'asc', 'asc'];
    const bookingStartDate = startDate ? moment(startDate) : null;
    const bookingEndDate = endDate ? moment(endDate) : null;
    const isHostBooking = isHostBookingSource(bookingSource);

    const offersWithSelectable = offers.map(offer => {
      const bookingStart = bookingStartDate
        ? bookingStartDate.format('YYYY-MM-DD')
        : moment();
      const bookingEnd = bookingEndDate
        ? bookingEndDate.format('YYYY-MM-DD')
        : moment();
      const offerStartMoment = moment(offer.bookingStartDate);
      const offerEndMoment = moment(offer.bookingEndDate);
      const withinSelectedDates =
        offerStartMoment.isBetween(bookingStart, bookingEnd, undefined, '[]') ||
        offerEndMoment.isBetween(bookingStart, bookingEnd, undefined, '[]');

      const offerContainsDateRange =
        bookingStartDate &&
        bookingStartDate.isBetween(
          offerStartMoment.format('YYYY-MM-DD'),
          offerEndMoment.format('YYYY-MM-DD'),
          undefined,
          '[]'
        ) &&
        bookingEndDate &&
        bookingEndDate.isBetween(
          offerStartMoment.format('YYYY-MM-DD'),
          offerEndMoment.format('YYYY-MM-DD'),
          undefined,
          '[]'
        );

      const isSelectable = withinSelectedDates || offerContainsDateRange;

      // If Host booking, check the offer's valid date range against the selected
      // calendar dates. If the offer isn't inside the selected date range,
      // mark it as inactive so the Host cannot select it.
      const inactive = isHostBooking && !isSelectable ? ' inactive' : '';

      return { ...offer, isSelectable, inactive };
    });

    return orderBy(offersWithSelectable, sortFields, sortDirects);
  };

  render() {
    const {
      loading,
      error,
      authenticatedNonAdmin,
      isAdmin,
      bookingSource,
      patronRewards,
      onlineRewards,
      selectedOffers,
      adminSelectedOffers: userSelectedOffers,
      startDate,
      endDate,
      property,
      submittedAccount,
      rewards
    } = this.props;
    const { activeTab } = this.state;

    const isHostBooking = isHostBookingSource(bookingSource);
    const offers = submittedAccount.accountNumber ? rewards : undefined;

    // this is the full list of offers, with the ones selected by the system having isSelected === true
    const autoSelectedOffers = combineOffersWithSelectedMeta(
      offers,
      selectedOffers
    );

    // this is the full list of offers, with the ones selected by the admin having isSelected === true
    const adminSelectedOffers =
      offers &&
      offers.map(offer => {
        const isSelected = !!find(
          userSelectedOffers,
          selectedOffer =>
            selectedOffer.patronReward.prizeCode ===
            offer.patronReward.prizeCode
        );

        return Object.assign({}, offer, {
          isSelected
        });
      });

    // If the user has selected offers, show the adminSelectedOffers. Otherwise show autoSelectedOffers.
    const offersList =
      userSelectedOffers?.length > 0 ? adminSelectedOffers : autoSelectedOffers;

    const pastUsedOffers = [
      ...(patronRewards || []),
      ...(onlineRewards || [])
    ].filter(
      offer =>
        (offer.isRedeemed() || offer.isExpired(new Date())) &&
        (offer.prizeCode !== WCH_HOTEL || isHostBooking)
    );

    const sortedOffers = this.sortOffers(offersList || []);
    const isMobile = isMobileUserAgent();

    return (
      <StyledOffers isHostBooking={isHostBooking}>
        <div className="row offers-header">
          <div className={`${!isHostBooking ? 'col-auto' : ''} your-offers`}>
            <StyledWindcreekTabs>
              <StyledWindcreekTab
                key={CURRENT_OFFERS_TAB}
                className={activeTab === CURRENT_OFFERS_TAB ? 'active' : ''}
                onClick={() => this.setActiveTab(CURRENT_OFFERS_TAB)}
              >
                Current
              </StyledWindcreekTab>
              <StyledWindcreekTab
                key={PAST_USED_OFFERS_TAB}
                className={activeTab === PAST_USED_OFFERS_TAB ? 'active' : ''}
                onClick={() => this.setActiveTab(PAST_USED_OFFERS_TAB)}
              >
                Past/Used
              </StyledWindcreekTab>
              {isHostBooking && !isMobile && (
                <StyledWindcreekTab
                  key={REQUEST_WAIT_LIST_TAB}
                  className={
                    activeTab === REQUEST_WAIT_LIST_TAB ? 'active' : ''
                  }
                  onClick={() => this.setActiveTab(REQUEST_WAIT_LIST_TAB)}
                >
                  Request Waitlist
                </StyledWindcreekTab>
              )}
            </StyledWindcreekTabs>
          </div>
          {!isHostBooking && !isAdmin && property !== PROPERTY_BETHLEHEM && (
            <div className="col promotion-container">
              <PromotionBanner offerError={error} offers={offers} />
            </div>
          )}
        </div>
        <div className="offers">
          {activeTab === CURRENT_OFFERS_TAB && (
            <CurrentOffers
              loading={loading}
              error={error}
              offersList={offersList}
              isHostBooking={isHostBooking}
              startDate={startDate}
              endDate={endDate}
              authenticatedNonAdmin={authenticatedNonAdmin}
              isAdmin={isAdmin}
              sortedOffers={sortedOffers}
              autoSelectedOffers={autoSelectedOffers}
              handleSelectedOffer={this.handleSelectedOffer}
            />
          )}
          {activeTab === PAST_USED_OFFERS_TAB && (
            <PastOffers
              authenticatedNonAdmin={authenticatedNonAdmin}
              pastUsedOffers={pastUsedOffers}
            />
          )}
          {isHostBooking && activeTab === REQUEST_WAIT_LIST_TAB && (
            <WaitlistFormContainer />
          )}
        </div>
      </StyledOffers>
    );
  }
}

Offers.propTypes = {
  offers: PropTypes.arrayOf(hotelOfferShape),
  selectedOffers: PropTypes.objectOf(hotelOfferShape),
  loading: PropTypes.bool,
  error: PropTypes.any,
  authenticatedNonAdmin: PropTypes.bool,
  property: PropTypes.string.isRequired,
  isAdmin: PropTypes.bool,
  bookingSource: PropTypes.string,
  isWaitListBooking: PropTypes.bool,
  rewardsData: PropTypes.shape({
    rewardsWithTimeZone: PropTypes.shape({
      rewardTimeZoneHelpText: PropTypes.string,
      patronRewards: PropTypes.arrayOf(
        PropTypes.shape({
          prizeType: PropTypes.string,
          prizeName: PropTypes.string,
          startDate: PropTypes.string,
          endDate: PropTypes.string,
          startDateUTC: PropTypes.string,
          endDateUTC: PropTypes.string,
          redeemed: PropTypes.number,
          maxPrizesPerPeriod: PropTypes.number,
          isNewOffer: PropTypes.bool,
          isSoftOffer: PropTypes.bool
        })
      )
    }),
    loading: PropTypes.bool,
    error: errorShape
  }),
  setAdminSelectedOffers: PropTypes.func,
  adminSelectedOffers: PropTypes.array,
  startDate: PropTypes.instanceOf(Date),
  endDate: PropTypes.instanceOf(Date),
  submittedAccount: PropTypes.objectOf(submittedAccountShape)
};

export default Offers;
